"use client"

import { ArtificialTurfCostResult } from "@/lib/artificial-turf-cost-calculator"
import { TURF_GRADE_LABELS } from "@/data/artificial-turf-costs"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  DollarSign,
  TrendingDown,
  CheckCircle,
  Layers,
  MapPin,
  Ruler,
  Crown,
  Hammer,
} from "lucide-react"
import { UnifiedShareButton } from "@/components/tools/shared"
import { buildToolUrl } from "@/lib/result-tool-urls"

interface ArtificialTurfCostResultsProps {
  result: ArtificialTurfCostResult
}

const usd = (n: number) => `$${Math.round(n).toLocaleString()}`
const usdRange = (r: { low: number; high: number }) =>
  r.low === r.high ? usd(r.low) : `${usd(r.low)} to ${usd(r.high)}`
const perSqFtRange = (r: { low: number; high: number }) =>
  r.low === r.high ? `$${r.low.toFixed(2)}` : `$${r.low.toFixed(2)} to $${r.high.toFixed(2)}`

export function ArtificialTurfCostResults({ result }: ArtificialTurfCostResultsProps) {
  if (!result || !result.region) {
    return null
  }

  const methodLabel = result.installMethod === "diy" ? "DIY" : "Professional"
  const maxBudget = Math.max(...result.budgetBreakdown.map((b) => b.high), 1)

  return (
    <div id="results" className="space-y-6">
      {/* Hero Card */}
      <Card className="p-6 md:p-8 bg-linear-to-br from-green-600 via-green-500 to-green-700 border-0 text-white overflow-hidden relative shadow-lg">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" aria-hidden="true" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" aria-hidden="true" />

        <div className="relative">
          <div className="absolute top-0 right-0">
            <UnifiedShareButton
              url={typeof window !== "undefined"
                ? `${window.location.origin}${buildToolUrl("artificial-turf-cost-calculator", {
                    zip: result.zipCode,
                    lawnSize: result.area,
                    turfGrade: result.turfGrade,
                    installMethod: result.installMethod,
                    removeExistingLawn: result.removeExistingLawn,
                  })}`
                : ""}
              title="Artificial Turf Cost Estimate"
              text={`Estimated cost to install ${result.area.toLocaleString()} sq ft of artificial turf`}
              variant="hero-overlay"
              size="sm"
            />
          </div>
          <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
            <div className="flex-shrink-0 p-4 bg-white/20 backdrop-blur-xs rounded-2xl">
              <Layers className="w-12 h-12 md:w-16 md:h-16" />
            </div>

            <div className="flex-1 text-center md:text-left">
              <h2 className="text-2xl md:text-4xl font-bold mb-2 tabular-nums">
                {usdRange(result.totalCost)}
              </h2>
              <p className="text-white/80 text-lg mb-1">
                Estimated {methodLabel.toLowerCase()} install, {perSqFtRange(result.costPerSqFt)} per sq ft
              </p>
              <p className="text-white/70 text-sm mb-4">
                Estimate range only, not a quote. Get local quotes before you budget.
              </p>

              <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                <span className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-xs px-3 py-1.5 rounded-full text-sm">
                  <Ruler className="w-4 h-4" />
                  {result.area.toLocaleString()} sq ft
                </span>
                <span className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-xs px-3 py-1.5 rounded-full text-sm">
                  <Layers className="w-4 h-4" />
                  {TURF_GRADE_LABELS[result.turfGrade]} grade
                </span>
                <span className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-xs px-3 py-1.5 rounded-full text-sm">
                  <MapPin className="w-4 h-4" />
                  {result.region}
                </span>
              </div>
            </div>
          </div>

          {/* Quick Metrics Grid */}
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
            <div className="bg-white/15 backdrop-blur-xs rounded-xl p-3 text-center">
              <p className="text-white/70 text-xs uppercase tracking-wider mb-1">Per sq ft</p>
              <p className="font-bold text-base md:text-lg tabular-nums">{perSqFtRange(result.costPerSqFt)}</p>
            </div>
            <div className="bg-white/15 backdrop-blur-xs rounded-xl p-3 text-center">
              <p className="text-white/70 text-xs uppercase tracking-wider mb-1">Turf material</p>
              <p className="font-bold text-base md:text-lg tabular-nums">{usdRange(result.materialCost)}</p>
            </div>
            <div className="bg-white/15 backdrop-blur-xs rounded-xl p-3 text-center">
              <p className="text-white/70 text-xs uppercase tracking-wider mb-1">Grade</p>
              <p className="font-bold text-sm md:text-base">{TURF_GRADE_LABELS[result.turfGrade]}</p>
            </div>
            <div className="bg-white/15 backdrop-blur-xs rounded-xl p-3 text-center">
              <p className="text-white/70 text-xs uppercase tracking-wider mb-1">Install</p>
              <p className="font-bold text-sm md:text-base">{methodLabel}</p>
            </div>
          </div>
        </div>
      </Card>

      {/* DIY vs Professional Comparison */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-green-600" />
            DIY vs professional
          </CardTitle>
          <CardDescription>
            Same {result.area.toLocaleString()} sq ft job, two ways to install it. Ranges are estimates, not quotes.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              className={`text-center p-5 rounded-xl border ${
                result.installMethod === "diy"
                  ? "bg-linear-to-br from-green-100 to-green-50 border-2 border-green-400"
                  : "bg-linear-to-br from-green-50 to-gray-50 border-green-200"
              }`}
            >
              <div className="flex items-center justify-center gap-2 mb-2">
                <Hammer className="w-4 h-4 text-green-700" />
                <p className="text-sm text-gray-600">DIY (materials + rental)</p>
              </div>
              <p className="text-2xl md:text-3xl font-bold text-green-700 tabular-nums">{usdRange(result.diyTotal)}</p>
              <p className="text-xs text-gray-500 mt-2">No labor line, your weekend</p>
            </div>

            <div
              className={`text-center p-5 rounded-xl border ${
                result.installMethod === "professional"
                  ? "bg-linear-to-br from-green-100 to-green-50 border-2 border-green-400"
                  : "bg-linear-to-br from-green-50 to-gray-50 border-green-200"
              }`}
            >
              <div className="flex items-center justify-center gap-2 mb-2">
                <Crown className="w-4 h-4 text-green-700" />
                <p className="text-sm text-gray-600">Professional (installed)</p>
              </div>
              <p className="text-2xl md:text-3xl font-bold text-green-700 tabular-nums">{usdRange(result.proTotal)}</p>
              <p className="text-xs text-gray-500 mt-2">Crew handles the full install</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cost Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            Where the money goes
          </CardTitle>
          <CardDescription>
            Estimated cost by line item for your {methodLabel.toLowerCase()} install
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {result.budgetBreakdown.map((item) => (
            <div
              key={item.category}
              className="p-4 bg-linear-to-br from-green-50/60 to-gray-50 rounded-xl border border-green-100"
            >
              <div className="flex justify-between items-baseline gap-3 mb-2">
                <div>
                  <h4 className="font-semibold text-green-900">{item.category}</h4>
                  <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>
                </div>
                <p className="text-sm font-semibold text-green-700 tabular-nums whitespace-nowrap">
                  {usdRange({ low: item.low, high: item.high })}
                  <span className="text-xs font-normal text-gray-500 ml-2">{item.percentage.toFixed(0)}%</span>
                </p>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                <div
                  className="h-1.5 rounded-full bg-green-500 transition-all"
                  style={{ width: `${(item.high / maxBudget) * 100}%` }}
                />
              </div>
            </div>
          ))}
          <p className="text-xs text-gray-500">
            Percentages are based on the midpoint of each range. Local quotes commonly land outside these ranges depending
            on base depth, yard access, and crew.
          </p>
        </CardContent>
      </Card>

      {/* Cost Saving Tips */}
      <Card className="border-green-200">
        <CardHeader className="bg-green-50 rounded-t-lg">
          <CardTitle className="flex items-center gap-2 text-green-800">
            <TrendingDown className="w-5 h-5" />
            Ways to spend less
          </CardTitle>
          <CardDescription>How to keep an artificial turf project on budget</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <ul className="space-y-2">
            {result.costSavingTips.map((tip, index) => (
              <li key={index} className="flex items-start gap-3 text-sm p-2 bg-green-50/50 rounded-lg">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{tip}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  )
}
